import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useKPIs } from '../contexts/KPIContext';
import { usePreventionModel } from '../contexts/PreventionModelContext';
function TrendSummary({
  label,
  current,
  baseline,
  unit,
  color
}: {
  label: string;
  current: number;
  baseline: number;
  unit: string;
  color: string;
}) {
  const diff = current - baseline;
  const pctChange = baseline !== 0 ? Math.abs(diff / baseline * 100).toFixed(0) : '—';
  const isUp = diff > 0;
  const isFlat = diff === 0;
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  return <span className="inline-flex items-center gap-1.5 text-xs" data-id="element-958">
      <span className="font-medium text-gray-600" data-id="element-959">{label}</span>
      <span className={`inline-flex items-center gap-0.5 font-semibold ${color}`} data-id="element-960">
        <Icon size={12} data-id="element-961" />
        {isFlat ? 'Flat' : `${isUp ? '+' : '−'}${pctChange}%`}
      </span>
      <span className="text-gray-400" data-id="element-962">
        ({current}
        {unit})
      </span>
    </span>;
}
export function KPITimelineChart({
  useNewModel = false
}: {
  useNewModel?: boolean;
}) {
  const oldContext = useKPIs();
  const newContext = usePreventionModel();
  const [expanded, setExpanded] = useState(false);
  // Get data depending on which context we're using
  let leadingData: any = null;
  let laggingData: any = null;
  if (useNewModel) {
    const {
      indicators
    } = newContext;
    leadingData = indicators.find(i => i.type === 'leading' && i.history.length > 0);
    laggingData = indicators.find(i => i.type === 'lagging' && i.history.length > 0);
  } else {
    const {
      kpis
    } = oldContext;
    leadingData = kpis.find(k => k.type === 'leading' && k.history.length > 0);
    laggingData = kpis.find(k => k.type === 'lagging' && k.history.length > 0);
  }
  if (!leadingData || !laggingData) {
    return null;
  }
  const chartData = leadingData.history.map((lh: any, index: number) => {
    const lagH = laggingData.history[index] || laggingData.history[laggingData.history.length - 1];
    return {
      date: lh.date,
      leadingValue: lh.value,
      laggingValue: lagH ? lagH.value : null
    };
  });
  return <div className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden" data-id="element-963">
      {/* Compact header — always visible */}
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors" data-id="element-964">
        <div className="flex items-center gap-4" data-id="element-965">
          <h3 className="text-xs font-semibold text-gray-900" data-id="element-966">
            Indicator Trends
          </h3>
          <div className="flex items-center gap-3 border-l border-gray-200 pl-4" data-id="element-967">
            <TrendSummary label={useNewModel ? leadingData.name : leadingData.metric} current={leadingData.currentValue} baseline={useNewModel ? leadingData.history[0]?.value || 0 : leadingData.baselineValue} unit={leadingData.unit} color="text-indigo-600" data-id="element-968" />
            <span className="text-gray-300" data-id="element-969">·</span>
            <TrendSummary label={useNewModel ? laggingData.name : laggingData.metric} current={laggingData.currentValue} baseline={useNewModel ? laggingData.history[0]?.value || 0 : laggingData.baselineValue} unit={laggingData.unit} color="text-amber-600" data-id="element-970" />
          </div>
        </div>
        <motion.div animate={{
        rotate: expanded ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-971">
          <ChevronDown size={16} className="text-gray-400" data-id="element-972" />
        </motion.div>
      </button>

      {/* Expandable chart */}
      <AnimatePresence data-id="element-973">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-974">
            <div className="px-4 pb-3" data-id="element-975">
              <div className="h-[140px] w-full" data-id="element-976">
                <ResponsiveContainer width="100%" height="100%" data-id="element-977">
                  <LineChart data={chartData} margin={{
                top: 5,
                right: 10,
                left: 0,
                bottom: 0
              }} data-id="element-978">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" data-id="element-979" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} data-id="element-980" />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} width={35} data-id="element-981" />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} width={35} data-id="element-982" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  fontSize: '12px'
                }} data-id="element-983" />
                    <Line yAxisId="left" type="monotone" dataKey="leadingValue" name={useNewModel ? leadingData.name : leadingData.title} stroke="#4f46e5" strokeWidth={2} dot={{
                  r: 3,
                  strokeWidth: 2
                }} activeDot={{
                  r: 5
                }} data-id="element-984" />
                    <Line yAxisId="right" type="monotone" dataKey="laggingValue" name={useNewModel ? laggingData.name : laggingData.title} stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3" dot={{
                  r: 3,
                  strokeWidth: 2
                }} activeDot={{
                  r: 5
                }} data-id="element-985" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 mt-1" data-id="element-986">
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500" data-id="element-987">
                  <span className="w-4 h-0.5 bg-indigo-500 rounded-full inline-block" data-id="element-988" />
                  {useNewModel ? leadingData.name : leadingData.title}
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500" data-id="element-989">
                  <span className="w-4 h-0.5 bg-amber-500 rounded-full inline-block border-dashed" style={{
                borderTop: '2px dashed #f59e0b',
                height: 0,
                background: 'none'
              }} data-id="element-990" />
                  {useNewModel ? laggingData.name : laggingData.title}
                </span>
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-1" data-id="element-991">
                Alignment does not imply causation.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}